<template>
  <div class="nft-mint">
    <div class="mint-card">
      <div class="card-header">
        <h2 class="title">Mint Your X CARS NFT</h2>
        <p class="subtitle">Join the exclusive collection of 10,000 unique pixel art cars</p>
      </div>

      <div class="mint-info">
        <div class="info-grid">
          <div class="info-item">
            <span class="label">Price</span>
            <span class="value">{{ NFT_CONFIG.formatPrice(NFT_CONFIG.PRICE_PER_NFT) }}</span>
          </div>
          <div class="info-item">
            <span class="label">Total Supply</span>
            <span class="value">{{ NFT_CONFIG.TOTAL_SUPPLY.toLocaleString() }}</span>
          </div>
          <div class="info-item">
            <span class="label">Minted</span>
            <span class="value">{{ mintedCount }}/{{ NFT_CONFIG.TOTAL_SUPPLY.toLocaleString() }}</span>
          </div>
          <div class="info-item">
            <span class="label">Remaining</span>
            <span class="value">{{ (NFT_CONFIG.TOTAL_SUPPLY - mintedCount).toLocaleString() }}</span>
          </div>
        </div>

        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ width: `${(mintedCount / NFT_CONFIG.TOTAL_SUPPLY) * 100}%` }"
          ></div>
        </div>
      </div>

      <div class="mint-controls">
        <div class="quantity-selector">
          <label class="quantity-label">Quantity</label>
          <div class="quantity-input">
            <button 
              @click="decreaseQuantity" 
              :disabled="quantity <= 1"
              class="quantity-btn"
            >
              -
            </button>
            <input 
              v-model.number="quantity" 
              type="number" 
              min="1" 
              :max="NFT_CONFIG.MAX_MINT_PER_TRANSACTION"
              class="quantity-field"
            />
            <button 
              @click="increaseQuantity" 
              :disabled="quantity >= NFT_CONFIG.MAX_MINT_PER_TRANSACTION"
              class="quantity-btn"
            >
              +
            </button>
          </div>
        </div>

        <div class="total-cost">
          <span class="cost-label">Total Cost:</span>
          <span class="cost-value">{{ NFT_CONFIG.formatPrice(NFT_CONFIG.calculateTotalCost(quantity)) }}</span>
        </div>

        <button 
          @click="handleMint"
          :disabled="!canMint || isMinting"
          class="mint-btn"
        >
          <svg v-if="isMinting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ getMintButtonText }}
        </button>
      </div>
    </div>

    <!-- Success Modal -->
    <div v-if="showSuccessModal" class="modal-overlay" @click="closeSuccessModal">
      <div class="modal-content" @click.stop>
        <div class="success-icon">
          <svg class="w-16 h-16 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <h3 class="success-title">Minting Successful!</h3>
        <p class="success-message">
          Congratulations! You have successfully minted {{ lastMintQuantity }} X CARS NFT{{ lastMintQuantity > 1 ? 's' : '' }}.
        </p>
        <button @click="closeSuccessModal" class="success-btn">
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useWalletStore } from '@/stores/wallet'
import { NFT_CONFIG } from '@/config/nft'
import { toast } from '@/utils/toast'

const walletStore = useWalletStore()

// State
const quantity = ref(1)
const isMinting = ref(false)
const mintedCount = ref(0) // Mock data - replace with actual contract data
const showSuccessModal = ref(false)
const lastMintQuantity = ref(0)

// Computed
const canMint = computed(() => {
  return walletStore.isConnected && 
         walletStore.isOnXLayer && 
         !isMinting.value &&
         quantity.value > 0 &&
         quantity.value <= NFT_CONFIG.MAX_MINT_PER_TRANSACTION
})

const getMintButtonText = computed(() => {
  if (!walletStore.isConnected) return 'Connect Wallet First'
  if (!walletStore.isOnXLayer) return 'Switch to X Layer'
  if (isMinting.value) return 'Minting...'
  return `Mint ${quantity.value} NFT${quantity.value > 1 ? 's' : ''}`
})

// Methods
const increaseQuantity = () => {
  if (quantity.value < NFT_CONFIG.MAX_MINT_PER_TRANSACTION) {
    quantity.value++
  }
}

const decreaseQuantity = () => {
  if (quantity.value > 1) {
    quantity.value--
  }
}

const handleMint = async () => {
  if (!canMint.value) return

  try {toast.success('Coming soon');return;
    isMinting.value = true
    lastMintQuantity.value = quantity.value
    
    // Call the wallet store mint function
    await walletStore.mintNFT(quantity.value)
    
    // Update minted count (in real implementation, fetch from contract)
    mintedCount.value += quantity.value
    
    // Show success modal
    showSuccessModal.value = true
    
    // Reset quantity
    quantity.value = 1
    
  } catch (error: any) {
    console.error('Minting failed:', error)
    toast.error('Minting Failed', error.message || 'Minting failed. Please try again.')
  } finally {
    isMinting.value = false
  }
}

const closeSuccessModal = () => {
  showSuccessModal.value = false
}
</script>

<style scoped>
.nft-mint {
  @apply w-full max-w-md mx-auto;
}

.mint-card {
  @apply bg-gray-900 rounded-2xl p-6 space-y-6 border border-gray-700;
}

.card-header {
  @apply text-center space-y-2;
}

.title {
  @apply text-2xl font-bold text-white;
}

.subtitle {
  @apply text-gray-400 text-sm;
}

.mint-info {
  @apply space-y-4;
}

.info-grid {
  @apply grid grid-cols-2 gap-4;
}

.info-item {
  @apply flex flex-col space-y-1;
}

.label {
  @apply text-gray-400 text-xs uppercase tracking-wide;
}

.value {
  @apply text-white font-semibold;
}

.progress-bar {
  @apply w-full bg-gray-700 rounded-full h-2;
}

.progress-fill {
  @apply bg-gradient-to-r from-green-400 to-blue-500 h-2 rounded-full transition-all duration-500;
}

.mint-controls {
  @apply space-y-4;
}

.quantity-selector {
  @apply space-y-2;
}

.quantity-label {
  @apply block text-white font-semibold text-sm;
}

.quantity-input {
  @apply flex items-center space-x-2;
}

.quantity-btn {
  @apply bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed;
}

.quantity-field {
  @apply flex-1 bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-center focus:outline-none focus:border-purple-500;
}

.total-cost {
  @apply flex justify-between items-center p-3 bg-gray-800 rounded-lg;
}

.cost-label {
  @apply text-gray-400;
}

.cost-value {
  @apply text-white font-bold text-lg;
}

.mint-btn {
  @apply w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center;
}

/* Modal Styles */
.modal-overlay {
  @apply fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50;
}

.modal-content {
  @apply bg-gray-900 rounded-2xl p-8 max-w-sm mx-4 text-center space-y-4;
}

.success-icon {
  @apply flex justify-center;
}

.success-title {
  @apply text-2xl font-bold text-white;
}

.success-message {
  @apply text-gray-300;
}

.success-btn {
  @apply bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200;
}
</style>